import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const PostList = ({ posts, onEdit, onDelete }) => {
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    // Filter posts based on search term
    const filtered = posts.filter(post => 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.author.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Sort posts
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      if (sortField === 'title') {
        comparison = a.title.localeCompare(b.title);
      } else if (sortField === 'category') {
        comparison = a.category.localeCompare(b.category);
      } else if (sortField === 'author') {
        comparison = a.author.localeCompare(b.author);
      } else if (sortField === 'comments') {
        comparison = a.comments - b.comments;
      } else if (sortField === 'views') {
        comparison = a.views - b.views;
      } else {
        // Default to createdAt
        comparison = new Date(a.createdAt) - new Date(b.createdAt);
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    setFilteredPosts(sorted);
  }, [posts, searchTerm, sortField, sortDirection]);
  
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? (
          <svg className="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
          </svg>
        ) : (
          <svg className="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        )}
      </span>
    );
  };
  
  return (
    <div>
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search posts by title, category, or author..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input pl-10 w-full"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="admin-table">
          <thead>
            <tr>
              <th 
                className="cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort('title')}
              >
                Title {renderSortIcon('title')}
              </th>
              <th 
                className="cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort('category')}
              >
                Category {renderSortIcon('category')}
              </th>
              <th 
                className="cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort('author')}
              >
                Author {renderSortIcon('author')}
              </th>
              <th 
                className="cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort('createdAt')}
              >
                Date {renderSortIcon('createdAt')}
              </th>
              <th 
                className="cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort('comments')}
              >
                Comments {renderSortIcon('comments')}
              </th>
              <th 
                className="cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort('views')}
              >
                Views {renderSortIcon('views')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPosts.length > 0 ? (
              filteredPosts.map(post => (
                <tr key={post._id}>
                  <td>
                    <Link
                      to={`/post/${post.slug || post._id}`}
                      className="text-primary hover:text-primary-dark"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {post.title}
                    </Link>
                  </td>
                  <td>
                    <span className="px-2 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium">
                      {post.category}
                    </span>
                  </td>
                  <td>{post.author}</td>
                  <td>{new Date(post.createdAt).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}</td>
                  <td>{post.comments}</td>
                  <td>{post.views}</td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        onClick={() => onEdit(post)}
                        className="admin-table-action edit"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
                        </svg>
                      </button>
                      <button
                        onClick={() => onDelete(post._id)}
                        className="admin-table-action delete"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center py-4">
                  {searchTerm ? 'No posts match your search criteria.' : 'No posts found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PostList;
